import * as dateFns from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import i18n from '../i18n';
import {
  RECURRENCE_TYPE,
  WEEKDAYS_LIST,
  WEEKDAYS_RULE,
} from '../components/Configuration/Modals/constants';

/**
 * Locale-aware summary of a recurrence rule, e.g.
 *   en: "Every week on Monday, Wednesday until 21 October 2026"
 *   no: "Hver uke på mandag, onsdag frem til og med 21. oktober 2026"
 *
 * The English output is byte-identical to the legacy `getRuleDescription`
 * (services/helpers.js), which is what the backend stores as
 * `ruleDescription`; keep it that way so stored and freshly rendered
 * strings never differ. Texts live under `recurrenceSummary` in the locales.
 */

const DATE_FORMAT = {
  en: { pattern: 'dd MMMM yyyy', locale: enUS },
  no: { pattern: 'd. MMMM yyyy', locale: nb },
};

export const normalizeLanguage = (language) => (/^en/i.test(String(language || '')) ? 'en' : 'no');

// English ordinal suffix for the "on the 21st" month-day form (legacy behaviour).
const withEnglishSuffix = (dayNumber) => {
  const i = dayNumber % 10;
  const j = dayNumber % 100;
  if (i === 1 && j !== 11) return `${dayNumber}st`;
  if (i === 2 && j !== 12) return `${dayNumber}nd`;
  if (i === 3 && j !== 13) return `${dayNumber}rd`;
  return `${dayNumber}th`;
};

const weekdayKey = (value) => WEEKDAYS_LIST.find((item) => parseInt(item.value, 10) === value)?.label;
const ordinalKey = (value) => WEEKDAYS_RULE.find((item) => parseInt(item.value, 10) === value)?.label;

export const describeRecurrence = (rule, language) => {
  if (!rule) return '';
  const lang = normalizeLanguage(language);
  const t = i18n.getFixedT(lang);
  const { freq, interval, byweekday = [], bymonthday = [], weekNumber, count, until } = rule;
  const days = byweekday || [];
  const many = interval > 1;
  const parts = [];

  // eslint-disable-next-line default-case
  switch (freq) {
    case RECURRENCE_TYPE.DAILY:
      parts.push(many ? t('recurrenceSummary.everyNDays', { n: interval }) : t('recurrenceSummary.everyDay'));
      break;
    case RECURRENCE_TYPE.WEEKLY: {
      parts.push(many ? t('recurrenceSummary.everyNWeeks', { n: interval }) : t('recurrenceSummary.everyWeek'));
      // legacy: an explicit `null` byweekday rendered no "on …" part at all
      if (byweekday !== null) {
        const names = WEEKDAYS_LIST
          .filter((item) => days.includes(parseInt(item.value, 10)))
          .map((item) => t(`recurrenceSummary.weekday.${item.label}`))
          .join(', ');
        parts.push(t('recurrenceSummary.onWeekdays', { days: names }));
      }
      break;
    }
    case RECURRENCE_TYPE.MONTHLY: {
      parts.push(many ? t('recurrenceSummary.everyNMonths', { n: interval }) : t('recurrenceSummary.everyMonth'));
      if (weekNumber) {
        parts.push(t('recurrenceSummary.onMonthWeekday', {
          ordinal: t(`recurrenceSummary.ordinal.${ordinalKey(weekNumber)}`),
          weekday: t(`recurrenceSummary.weekdayDefinite.${weekdayKey(days[0])}`),
        }));
      } else {
        const monthDay = (bymonthday || [])[0];
        parts.push(t('recurrenceSummary.onMonthDay', {
          day: lang === 'en' ? withEnglishSuffix(monthDay) : monthDay,
        }));
      }
      break;
    }
  }
  if (!parts.length) return '';

  if (until) {
    const { pattern, locale } = DATE_FORMAT[lang];
    parts.push(t('recurrenceSummary.until', { date: dateFns.format(new Date(until), pattern, { locale }) }));
  } else if (count) {
    parts.push(t('recurrenceSummary.times', { n: count }));
  }

  return parts.join(' ');
};
